import {
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Button,
  useToast,
  VStack,
  Box,
  useRadio,
  UseRadioProps,
  useRadioGroup,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { WipConfig } from 'packages/creditAgreements/ui/types';

export const queryInputTypeIterable = [
  ...CreditAgreement.Config.CommonPostgresQuery.Query.PostgresQueryTypeIterable,
  'singleQuery',
  'multiQuery',
] as const;
export type PortfolioCovenantQueryInputType = typeof queryInputTypeIterable[number];

export const isPortfolioCovenantQueryInputType = (
  test: any,
): test is PortfolioCovenantQueryInputType => {
  return test && _.isString(test) && _.includes(queryInputTypeIterable, test);
};

export type PortfolioCovenantQueryInput = {
  queryInputKey: string;
  queryInputType: PortfolioCovenantQueryInputType;
};

export const queryInputTypeToIcon: Record<
  PortfolioCovenantQueryInputType,
  {
    type: PortfolioCovenantQueryInputType;
    label?: string;
    icon: string;
    tooltip: string;
    description: string;
  }
> = {
  singleQuery: {
    type: 'singleQuery',
    label: 'Single Date Query (SQL)',
    icon: 'calendar',
    tooltip: 'Single date',
    description: 'Query data to return a single set of values.',
  },
  multiQuery: {
    type: 'multiQuery',
    label: 'Recurring Date Query (SQL)',
    icon: 'calendar-days',
    tooltip: 'Recurring date',
    description:
      'Query data to return a multiple set of values based on a configured recurring date.',
  },
  principalBalanceDailyAverage: {
    type: 'principalBalanceDailyAverage',
    icon: 'scale-balanced',
    tooltip: 'principalBalanceDailyAverage',
    description: 'Get the average principal balance within a period of time.',
  },
  principalBalancePointInTime: {
    type: 'principalBalancePointInTime',
    icon: 'clock',
    tooltip: 'principalBalancePointInTime',
    description: 'Get the principal balance for a specific day.',
  },
  tAccountsSum: {
    type: 'tAccountsSum',
    label: 'T-Accounts Sum',
    icon: 'table-list',
    tooltip: 'tAccountsSum',
    description:
      'Get the sum value of any configured expenses and fees over a range of time.',
  },
  orgProvidedInput: {
    type: 'orgProvidedInput',
    icon: 'sitemap',
    tooltip: 'orgProvidedInput',
    description: 'Get the value of a configured input for your organization.',
  },
};

type RadioCardProps = {
  radioProps: UseRadioProps;
  type: PortfolioCovenantQueryInputType;
  icon: string;
  description: string;
  label?: string;
  isDisabled?: boolean;
};

const RadioCard = ({
  radioProps,
  type,
  icon,
  description,
  label,
  isDisabled = false,
}: RadioCardProps) => {
  const { getInputProps, getCheckboxProps, state } = useRadio(radioProps);

  const input = getInputProps();
  const checkbox = getCheckboxProps();

  return (
    <Tooltip
      label={
        isDisabled
          ? 'Reach out to your Finley administrator to configure an Org Provided Input'
          : undefined
      }
    >
      <Box as="label">
        <Input {...input} isDisabled={isDisabled} />
        <Flex
          {...checkbox}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          borderWidth="1px"
          borderRadius="md"
          color={isDisabled ? colors.black[50] : undefined}
          _checked={{
            borderColor: colors.gray[900],
            borderWidth: '2px',
            backgroundColor: colors.aurora[50],
          }}
          _hover={{
            backgroundColor: isDisabled ? undefined : colors.gray[100],
          }}
          padding="8px 16px"
          alignItems="center"
        >
          <Icon
            icon={state.isChecked ? 'circle-dot' : 'circle'}
            type={state.isChecked ? 'solid' : 'regular'}
            color={state.isChecked ? colors.info : colors.gray[200]}
          />
          <Box ml="16px">
            <Flex
              alignItems="center"
              mb="8px"
              color={isDisabled ? colors.black[50] : undefined}
            >
              <Icon icon={icon} />
              <Text ml="16px" fontWeight={500}>
                {label ?? commonUtils.toTitleCase(type)}
              </Text>
            </Flex>
            <Text color={isDisabled ? colors.black[50] : colors.black[70]}>
              {description}
            </Text>
          </Box>
        </Flex>
      </Box>
    </Tooltip>
  );
};

const AddQueryInputRadioCards = ({
  queryType,
  setQueryType,
  mode,
  disableOrgProvidedInputAdd,
}: {
  queryType: PortfolioCovenantQueryInputType | undefined;
  setQueryType: React.Dispatch<
    React.SetStateAction<PortfolioCovenantQueryInputType | undefined>
  >;
  mode: 'add' | 'duplicate';
  disableOrgProvidedInputAdd: boolean;
}) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'queryInputType',
    defaultValue: 'singleQuery',
    onChange: (newValue: PortfolioCovenantQueryInputType) => {
      setQueryType(newValue);
    },
    value: queryType,
  });

  const group = getRootProps();

  return (
    <VStack spacing="16px" alignItems="left" {...group}>
      {mode === 'add' &&
        _.map(queryInputTypeToIcon, ({ type, icon, description, label }) => {
          const radio = getRadioProps({
            value: type,
            // !TODO @eldarshamukhamedov Remove these if we ever get around to upgrading to Chakra 2.x
            crossOrigin: undefined,
            onPointerEnterCapture: undefined,
            onPointerLeaveCapture: undefined,
          });
          return (
            <RadioCard
              key={type}
              radioProps={radio}
              type={type}
              icon={icon}
              description={description}
              label={label}
              isDisabled={
                type === 'orgProvidedInput' && disableOrgProvidedInputAdd
              }
            />
          );
        })}
      {mode === 'duplicate' &&
        queryType &&
        (() => {
          const { type, icon, description, label } = queryInputTypeToIcon[
            queryType
          ];
          const selectedQueryTypeRadioProps = getRadioProps({
            value: type,
            // !TODO @eldarshamukhamedov Remove these if we ever get around to upgrading to Chakra 2.x
            crossOrigin: undefined,
            onPointerEnterCapture: undefined,
            onPointerLeaveCapture: undefined,
          });

          return (
            <RadioCard
              key={type}
              radioProps={selectedQueryTypeRadioProps}
              type={type}
              icon={icon}
              description={description}
              label={label}
            />
          );
        })()}
    </VStack>
  );
};

type AddQueryInputModalProps = {
  wipConfig: WipConfig;
  isOpen: boolean;
  onClose: () => void;
  onClickAdd: ({
    queryKeyInput,
    queryTypeInput,
  }: {
    queryKeyInput: string;
    queryTypeInput: PortfolioCovenantQueryInputType;
  }) => Promise<void>;
  initialQueryType: PortfolioCovenantQueryInputType;
  isAddLoading: boolean;
  mode: 'add' | 'duplicate';
};
export const AddQueryInputModal = ({
  wipConfig,
  isOpen,
  onClose,
  onClickAdd,
  initialQueryType,
  isAddLoading,
  mode,
}: AddQueryInputModalProps) => {
  const toast = useToast();

  const [queryKey, setQueryKey] = useState<string>('');
  const [queryType, setQueryType] = useState<
    PortfolioCovenantQueryInputType | undefined
  >(initialQueryType);

  const doOrgProvidedInputKeysExist = !!wipConfig.orgProvidedInputKeys.length;

  useEffect(() => {
    setQueryType(initialQueryType);
  }, [initialQueryType]);

  const onCloseModal = () => {
    onClose();
    setQueryKey('');
    setQueryType(initialQueryType);
  };

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`${commonUtils.toTitleCase(mode)} Query`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb="16px" fontWeight={600}>
            Query Key:
          </Text>
          <Input
            value={queryKey}
            onChange={
              (event) => setQueryKey(_.camelCase(event.target.value)) // only allow camelCase query key
            }
          />
          <Text mt="8px" fontSize="14px" color={colors.black[60]}>
            Auto-formatted in camelCase. This cannot be changed later.
          </Text>
          <Text mt="24px" mb="16px" fontWeight={600}>
            Query Type:
          </Text>
          <AddQueryInputRadioCards
            queryType={queryType}
            setQueryType={setQueryType}
            mode={mode}
            disableOrgProvidedInputAdd={
              mode === 'add' && !doOrgProvidedInputKeysExist // we disable orgProvidedInput add option if no org provided input keys exist
            }
          />
        </ModalBody>

        <ModalFooter mt="24px">
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onCloseModal}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            color="white"
            onClick={async () => {
              if (queryKey && queryType) {
                try {
                  await onClickAdd({
                    queryKeyInput: queryKey,
                    queryTypeInput: queryType,
                  });
                } catch (err) {
                  toast({
                    ...errorToastConfig,
                    id: 'errorAddQueryInput',
                    title: 'Error when adding query input. Please try again',
                  });
                } finally {
                  onCloseModal();
                }
              } else {
                const missingFields = _.compact([
                  !queryKey ? 'Query Key' : '',
                  !queryType ? 'Query Type' : '',
                ]);
                toast({
                  ...errorToastConfig,
                  id: 'add-query-input-missing-info',
                  title: `Fill in all required fields: ${missingFields.join(
                    ', ',
                  )}`,
                });
              }
            }}
            isLoading={isAddLoading}
          >
            {`${commonUtils.toTitleCase(mode)} Query`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
